// src/components/Orgs/OrgView.jsx
import React, { useState, useEffect } from 'react';
import { orgsApi } from '../../services/api';
import { Building2, Plus, Edit2, Trash2, X, Check, FolderKanban, Users, AppWindow } from 'lucide-react';

const PLAN_COLORS = {
  free:       { bg: 'rgba(100,116,139,.2)', color: '#94a3b8', label: 'Free' },
  starter:    { bg: 'rgba(34,197,94,.15)',  color: '#22c55e', label: 'Starter' },
  pro:        { bg: 'rgba(59,130,246,.15)', color: '#60a5fa', label: 'Pro' },
  enterprise: { bg: 'rgba(139,92,246,.15)',color: '#a78bfa', label: 'Enterprise' },
};
const LOGO_COLORS = ['#3b82f6','#8b5cf6','#22c55e','#f59e0b','#ef4444','#06b6d4','#ec4899','#84cc16'];

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,.6)', backdropFilter: 'blur(4px)' }}>
      <div className="relative w-full max-w-lg rounded-xl p-6"
        style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)', boxShadow: '0 25px 50px rgba(0,0,0,.5)' }}>
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold">{title}</h2>
          <button onClick={onClose} style={{ color: 'var(--text-3)', background: 'none', border: 'none', cursor: 'pointer' }}>
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-2)' }}>{label}</label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
  background: 'var(--bg-tertiary)', border: '1px solid var(--border)',
  color: 'var(--text)', outline: 'none',
};

const EMPTY_FORM = { name: '', description: '', plan: 'pro', logoColor: '#3b82f6' };

export default function OrgView() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'create' | org object
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  async function load() {
    setLoading(true);
    try { setOrgs(await orgsApi.list()); } catch { /* demo fallback via backend */ }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  function openCreate() { setForm(EMPTY_FORM); setModal('create'); }
  function openEdit(org) { setForm({ name: org.name, description: org.description || '', plan: org.plan || 'pro', logoColor: org.logoColor || '#3b82f6' }); setModal(org); }

  async function save() {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (modal === 'create') { await orgsApi.create(form); }
      else { await orgsApi.update(modal._id, form); }
      setModal(null);
      load();
    } finally { setSaving(false); }
  }

  async function confirmDelete(id) {
    await orgsApi.delete(id);
    setDeleteId(null);
    load();
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 shrink-0 flex items-center gap-4"
        style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
        <Building2 size={20} style={{ color: '#60a5fa' }} />
        <div>
          <h1 className="text-base font-semibold">Organizations</h1>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-3)' }}>Manage top-level organizations and their settings</p>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex gap-2">
            <Chip label={`${orgs.length} orgs`} color="#3b82f6" />
            <Chip label={`${orgs.reduce((s,o) => s + (o.memberCount||0), 0)} members`} color="#22c55e" />
          </div>
          <button onClick={openCreate}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium"
            style={{ background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)', cursor: 'pointer' }}>
            <Plus size={14} /> New Organization
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading ? (
          <div className="text-sm" style={{ color: 'var(--text-3)' }}>Loading…</div>
        ) : orgs.length === 0 ? (
          <EmptyState onAdd={openCreate} />
        ) : (
          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))' }}>
            {orgs.map(org => (
              <OrgCard key={org._id} org={org}
                onEdit={() => openEdit(org)}
                onDelete={() => setDeleteId(org._id)} />
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {modal && (
        <Modal title={modal === 'create' ? 'New Organization' : 'Edit Organization'} onClose={() => setModal(null)}>
          <Field label="Organization Name *">
            <input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Acme Corp" />
          </Field>
          <Field label="Description">
            <textarea style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description…" />
          </Field>
          <Field label="Plan">
            <select style={{ ...inputStyle, cursor: 'pointer' }} value={form.plan} onChange={e => setForm(f => ({ ...f, plan: e.target.value }))}>
              {Object.entries(PLAN_COLORS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </Field>
          <Field label="Logo Color">
            <div className="flex gap-2 flex-wrap">
              {LOGO_COLORS.map(c => (
                <button key={c} onClick={() => setForm(f => ({ ...f, logoColor: c }))}
                  style={{ width: 28, height: 28, borderRadius: '50%', background: c, border: form.logoColor === c ? '3px solid white' : '3px solid transparent', cursor: 'pointer', outline: 'none', transition: 'border-color .15s' }} />
              ))}
            </div>
          </Field>
          <div className="flex justify-end gap-2 mt-6">
            <button onClick={() => setModal(null)}
              style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>
              Cancel
            </button>
            <button onClick={save} disabled={saving || !form.name.trim()}
              style={{ padding: '7px 16px', borderRadius: 6, background: 'rgba(59,130,246,.2)', border: '1px solid rgba(59,130,246,.4)', color: '#60a5fa', cursor: 'pointer', fontSize: 13, fontWeight: 600, opacity: saving ? .5 : 1 }}>
              {saving ? 'Saving…' : (modal === 'create' ? 'Create' : 'Save Changes')}
            </button>
          </div>
        </Modal>
      )}

      {/* Delete Confirm */}
      {deleteId && (
        <Modal title="Delete Organization?" onClose={() => setDeleteId(null)}>
          <p className="text-sm mb-5" style={{ color: 'var(--text-2)' }}>
            This will permanently delete the organization and all its associated data. This action cannot be undone.
          </p>
          <div className="flex justify-end gap-2">
            <button onClick={() => setDeleteId(null)} style={{ padding: '7px 16px', borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-2)', cursor: 'pointer', fontSize: 13 }}>Cancel</button>
            <button onClick={() => confirmDelete(deleteId)} style={{ padding: '7px 16px', borderRadius: 6, background: 'rgba(239,68,68,.2)', border: '1px solid rgba(239,68,68,.4)', color: '#f87171', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Chip({ label, color }) {
  return (
    <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${color}18`, color, border: `1px solid ${color}30` }}>
      {label}
    </span>
  );
}

function OrgCard({ org, onEdit, onDelete }) {
  const plan = PLAN_COLORS[org.plan] || PLAN_COLORS.pro;
  const initials = org.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="rounded-xl p-5 flex flex-col gap-4" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold text-white shrink-0"
          style={{ background: `linear-gradient(135deg, ${org.logoColor || '#3b82f6'}, ${org.logoColor || '#3b82f6'}99)` }}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm truncate">{org.name}</span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 20, background: plan.bg, color: plan.color }}>{plan.label}</span>
          </div>
          <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--text-3)' }}>{org.description || 'No description'}</p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <IconBtn icon={Edit2} onClick={onEdit} />
          <IconBtn icon={Trash2} onClick={onDelete} danger />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <StatBox icon={FolderKanban} label="Projects" value={org.projectCount ?? 0} color="#3b82f6" />
        <StatBox icon={AppWindow} label="Apps" value={org.appCount ?? 0} color="#8b5cf6" />
        <StatBox icon={Users} label="Members" value={org.memberCount ?? 0} color="#22c55e" />
      </div>

      <div className="text-xs mono" style={{ color: 'var(--text-3)' }}>
        {org.slug ? `/${org.slug}` : '—'}
        {org.createdAt && ` · Created ${new Date(org.createdAt).toLocaleDateString()}`}
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  return (
    <div className="rounded-lg p-2.5 text-center" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)' }}>
      <Icon size={13} style={{ color, margin: '0 auto 3px' }} />
      <div className="text-sm font-bold">{value}</div>
      <div className="text-xs" style={{ color: 'var(--text-3)' }}>{label}</div>
    </div>
  );
}

function IconBtn({ icon: Icon, onClick, danger }) {
  return (
    <button onClick={onClick} style={{ padding: 5, borderRadius: 6, background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: danger ? '#f87171' : 'var(--text-3)', cursor: 'pointer', lineHeight: 0 }}
      className="hover:opacity-80">
      <Icon size={13} />
    </button>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 gap-4">
      <Building2 size={40} style={{ color: 'var(--text-3)' }} />
      <div className="text-sm font-medium">No organizations yet</div>
      <p className="text-xs text-center max-w-xs" style={{ color: 'var(--text-3)' }}>Create your first organization to start managing projects, applications, and developers.</p>
      <button onClick={onAdd}
        className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
        style={{ background: 'rgba(59,130,246,.2)', color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)', cursor: 'pointer' }}>
        <Plus size={14} /> New Organization
      </button>
    </div>
  );
}
